import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DndContext } from "@dnd-kit/core";

import GameTitle from "../../components/game/GameTitle";
import Text from "../../components/ui/Text";
import Mask from "../../components/ui/Mask";
import useCtaStore from "../../hooks/store/useCtaStore";
import useStore from "../../hooks/store/useStore";
import useEventTracking from "../../services/EventTracking/useEventTracking";
import ctaHandler7 from "../../utils/ctaHandlers/ctaHandler7";
import { QACardType } from "../../types";

import AnimatedCardPanel from "./components/AnimatedCardPanel";
import BlueFrame from "./components/BlueFrame";
import ButtonHandler from "./components/ButtonHandler";
import CardHandler from "./components/CardHandler";
import CardPanel from "./components/CardPanel";
import Playmate from "./components/Playmate";
import QACDialog from "./components/QACDialog";
import HelpModel from "./components/HelpModel";
import { isRect, isWaiting } from "./utils/judgements";
import {
  CenterCardDuration,
  FlipCardDuration,
  LandingCardPanelDuration,
  LandingPlaymateDuration,
  RotateCardDuration,
  qacAudioData,
} from "./constants";
import { QACGameState } from "./types";

export default function QACards() {
  const { t } = useTranslation();

  const frontPlayerRef = useRef<HTMLAudioElement>(null);
  const backPlayerRef = useRef<HTMLAudioElement>(null);

  const [gameState, setGameState] = useState<QACGameState>("beforeLanding");
  const [currentCard, setCurrentCard] = useState<QACardType | null>(null);
  const [isDragging, setIsDragging] = useState<QACardType | null>(null);
  const [frontAudioIndex, setFrontAudioIndex] = useState(0);
  const [backAudioIndex, setBackAudioIndex] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [isRotateHelpShowed, setIsRotateHelpShowed] = useState(false);
  const [isFirstPlay, setIsFirstPlay] = useState(true);

  const { userInformation } = useStore((state) => state.user);
  const { setShowingCtaETId } = useCtaStore((state) => state.cta);

  const mutation = useEventTracking();

  const isWaitingPhase = isWaiting(gameState);

  const playOn = async (
    el: HTMLAudioElement | null,
    src: string,
    onEnded?: () => void,
  ) => {
    if (!el) return;

    el.onended = null;
    el.pause();
    el.src = src;
    el.load();
    el.currentTime = 0;

    try {
      await el.play();

      if (onEnded) {
        el.onended = () => {
          el.onended = null;

          onEnded();
        };
      }
    } catch {}
  };

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    const activeId = active.id as QACardType;

    setIsDragging(null);

    if (over && over.id === "droppable") {
      const frontIndex = Math.random() >= 0.5 ? 0 : 1;

      setCurrentCard(activeId);
      setFrontAudioIndex(frontIndex);
      setBackAudioIndex(0);
      setGameState("frontPlaying");

      const url = qacAudioData[activeId].frontAudio[frontIndex].audio;

      await playOn(frontPlayerRef.current, url, handleAudioFrontEnded);
    }
  };

  const handleflip = () => {
    setGameState("cardFlippingAndCentering");

    setTimeout(() => {
      setGameState("waitingForRotating");

      if (!isRotateHelpShowed) {
        setShowHelp(true);
        setIsRotateHelpShowed(true);
      }
    }, FlipCardDuration);
  };

  const handleRotate = () => {
    if (!currentCard) return;

    setGameState("cardRotating");

    setTimeout(() => {
      const limit = isRect(currentCard) ? 4 : 5;
      const nextIndex = backAudioIndex + 1 === limit ? 0 : backAudioIndex + 1;

      setBackAudioIndex(nextIndex);
      setGameState("waitingForRotating");
    }, RotateCardDuration);
  };

  const handleOk = async () => {
    if (!currentCard) return;

    setGameState("backPlaying");

    const data =
      qacAudioData[currentCard].backAudio(frontAudioIndex)[backAudioIndex];
    const url = data.audio;
    const onEnded = data.isAnswer
      ? handleAudioBackCorrectEnded
      : handleAudioBackEnded;

    await playOn(backPlayerRef.current, url, onEnded);
  };

  const handleAudioFrontEnded = () => {
    setGameState("waitingForFlipping");

    setTimeout(() => {
      if (isFirstPlay) {
        setIsFirstPlay(false);
      } else {
        ctaHandler7(userInformation, setShowingCtaETId);
      }
    }, 150);

    // Written here to prevent animations from being disrupted by POST requests
    if (currentCard)
      setTimeout(
        () =>
          mutation.mutate({
            pageId: "Q & A Cards",
            eventId: currentCard,
          }),
        450,
      );
  };

  const handleAudioBackCorrectEnded = () =>
    setGameState("waitingForNewDragging");

  const handleAudioBackEnded = () => {
    setGameState("cardCentering");

    setTimeout(() => {
      setGameState("waitingForRotating");
    }, CenterCardDuration);
  };

  useEffect(() => {
    setGameState("landing1");

    setTimeout(() => setGameState("landing2"), LandingCardPanelDuration);

    setTimeout(() => {
      setGameState("waitingForDragging");
      setShowHelp(true);
    }, LandingCardPanelDuration + LandingPlaymateDuration);
  }, []);

  return (
    <div className="flex w-full flex-1 flex-col">
      <div className="mx-auto w-full max-w-[480px]">
        <GameTitle
          text="Q & A Cards"
          onHelpClick={() => {
            if (isWaitingPhase) setShowHelp(true);
          }}
        />
      </div>

      {showHelp ? (
        <HelpModel gameState={gameState} setShowHelp={setShowHelp} />
      ) : null}

      {gameState === "frontPlaying" ? <Mask /> : null}

      <DndContext
        onDragEnd={handleDragEnd}
        onDragStart={(event) => {
          const id = event.active.id as QACardType;
          setIsDragging(id);
        }}
      >
        <div className="flex w-full flex-1 flex-col">
          <div className="relative flex-1 bg-gradient-to-b from-pink1 to-pink2">
            <div className="relative mx-auto h-full w-full max-w-[390px] tablet:max-w-[480px]">
              {gameState === "waitingForDragging" ? (
                <div className="mb-4 mt-2 h-40 px-6">
                  <Text normal className="whitespace-pre-line">
                    {t("qaCardsDesc")}
                  </Text>
                </div>
              ) : (
                <div className="mt-9 tablet:mt-14">
                  <QACDialog
                    gameState={gameState}
                    currentCard={currentCard}
                    frontAudioIndex={frontAudioIndex}
                    backAudioIndex={backAudioIndex}
                  />
                </div>
              )}

              <div className="relative z-10 mt-[168px] flex w-full flex-col items-center tablet:mt-[243px]">
                <Playmate gameState={gameState} />

                <CardHandler
                  gameState={gameState}
                  currentCard={currentCard}
                  frontAudioIndex={frontAudioIndex}
                  backAudioIndex={backAudioIndex}
                />

                <ButtonHandler
                  gameState={gameState}
                  onFlip={handleflip}
                  onRotate={handleRotate}
                  onOk={handleOk}
                />

                <BlueFrame isDragging={isDragging} />
              </div>
            </div>
          </div>

          <div className="mx-auto w-full max-w-[390px]">
            {gameState === "landing1" ? (
              <AnimatedCardPanel />
            ) : (
              <CardPanel gameState={gameState} currentCard={currentCard} />
            )}
          </div>
        </div>
      </DndContext>

      <audio ref={frontPlayerRef} preload="none" playsInline />
      <audio ref={backPlayerRef} preload="none" playsInline />
    </div>
  );
}
